import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { questionAnswered, questionErrored } from 'actions/questionnaires';
import { Mode, DynamicUpdateAction } from 'common/questionnaires/types';
import dynamicUpdateProp from 'common/questionnaires/questionDataProp';
import { getGiinErrorMessage } from 'common/questionnaires/giinHelper';

const QuestionGiinField = ({
  id,
  name,
  isForm,
  dynamicUpdate,
  mode, fieldLength,
  fieldSeperator,
  runValidation,
  dispatch,
  fields
}) => {
  const defaultValue = (new Array(fieldLength)).fill({ value: '', changed: false });

  const [giin, setGiin] = useState(defaultValue);

  const inputRef = [...Array(fieldLength).keys()].map(x => useRef(null));
  const numRegex = /^["'0-9'"]*$/;
  const lettersRegex = /[A-Za-z]/;
  const numLettersRegex = /[A-Za-z0-9]/;

  function onKeyHandler(event, index) {
    if (event === null) {
      return;
    }

    if (event.shiftKey || event.keyCode === 9 || event.keyCode === 16) {
      return;
    }

    if (event.keyCode === 8) {
      if (index !== 0) {
        const previous = inputRef[index - 1];
        if (previous) {
          previous.current.focus();
        }
      }
    } else {
      const next = inputRef[index + 1];
      if (next && event.target.value !== '') {
        next.current.focus();
      }
    }
  }

  function handleChange(event, fieldIndex) {
    if (event === null || event.target === null) {
      return;
    }

    if (fieldIndex == null) {
      return;
    }

    if (event.target.value) {
      if ((!numLettersRegex.test(event.target.value) && fieldIndex <= 5)
        || (!numRegex.test(event.target.value) && (fieldIndex >= 6 && fieldIndex <= 10))
        || (!lettersRegex.test(event.target.value) && (fieldIndex >= 11 && fieldIndex <= 12))
        || (!numRegex.test(event.target.value) && fieldIndex >= 13)
        || event.target.value.length > 1) {
        return;
      }
    }

    setGiin(giin.map((fieldInput, i) => (i === fieldIndex ? {
      changed: true,
      value: event.target.value
    } : fieldInput)));
  }

  if (giin !== '' && dynamicUpdate && dynamicUpdate.conditionMet
     && (dynamicUpdate.action === DynamicUpdateAction.DisableAndClear || dynamicUpdate.action === DynamicUpdateAction.HideAndClear)) {
    setGiin('');
  }

  const createGiinString = () => {
    let giinString = '';
    giin.forEach((element) => {
      giinString = giinString.concat(element.value);
    });

    return giinString;
  };

  const validateQuestion = () => {
    const giinString = createGiinString();

    const requiredInput = fields.find(i => i.required === true);
    const isRequired = requiredInput !== undefined && requiredInput.required;
    const customError = getGiinErrorMessage(giinString, fieldLength, isRequired);
    const isValid = (!customError);

    if (isValid) {
      dispatch(questionAnswered({ id, answer: giinString, mode, isForm, name }));
    } else if (runValidation && !isValid && (!dynamicUpdate || !dynamicUpdate.conditionMet)) {
      dispatch(questionErrored({ id, mode, isForm, customError }));
    } else {
      dispatch(questionAnswered({ id, answer: '', mode, isForm, name }));
    }
  };

  useEffect(() => {
    validateQuestion();
  }, [giin, runValidation, dynamicUpdate]);

  const disabled = (dynamicUpdate && dynamicUpdate.conditionMet && dynamicUpdate.action === DynamicUpdateAction.DisableAndClear);

  return (
    <div id={ id }>
      <p>Please enter your GIIN in the correct format using capital letters and numbers only.</p>
      {Array.from({ length: fieldLength }, (element, index) => (
        <span key={ `item-${ index }` }>
          <input
            id={ `giin-field-${ index + 1 }` }
            name={ name }
            aria-label={ name }
            disabled={ disabled }
            className="giin-box"
            ref={ inputRef[index] ? inputRef[index] : '' }
            maxLength={ 1 }
            type="text"
            onKeyUp={ e => onKeyHandler(e, index) }
            onChange={ e => handleChange(e, index) }
            value={ giin[index] && giin[index].value && !disabled ? giin[index].value : '' }
          />
          { fieldSeperator && (fieldSeperator.indexOf(index) >= 0) && <span className="box-input-seperator">-</span>}
        </span>
      ))}
    </div>
  );
};

QuestionGiinField.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  isForm: PropTypes.bool,
  dynamicUpdate: dynamicUpdateProp,
  mode: PropTypes.number.isRequired,
  dispatch: PropTypes.func.isRequired,
  fieldLength: PropTypes.number,
  fieldSeperator: PropTypes.arrayOf(PropTypes.number),
  runValidation: PropTypes.bool,
  fields: PropTypes.arrayOf(PropTypes.shape({
    placeholder: PropTypes.string,
    required: PropTypes.bool
  })).isRequired,
};

QuestionGiinField.defaultProps = {
  isForm: false,
  dynamicUpdate: null,
  fieldLength: 16,
  fieldSeperator: [5, 10, 12],
  runValidation: false,
  name: null,
};

const mapDispatchToProps = dispatch => ({ dispatch });

const mapStateToProps = (state) => {
  const {
    app: {
      onlineConfig
    },
    formQuestionnaire: {
      runValidation,
    }
  } = state;

  return {
    mode: (onlineConfig && onlineConfig.preQualifyingQuestionnaireMode) ? onlineConfig.preQualifyingQuestionnaireMode : Mode.Crs,
    fieldSeperator: [5, 10, 12],
    runValidation
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(QuestionGiinField);
